import React, { useState } from 'react';
import { FileUpload as PrimeFileUpload } from 'primereact/fileupload';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Divider } from 'primereact/divider';

const FileUpload = ({ 
    onFilesUploaded = () => {}, 
    onResizeChange = () => {}, 
    onQualityChange = () => {},
    disabled = false, 
    conversionProgress = [] 
}) => {
    const [error, setError] = useState('');
    const [resizeOption, setResizeOption] = useState('original');
    const [quality, setQuality] = useState(80);
    const [width, setWidth] = useState(1920);
    const [height, setHeight] = useState(1080);
    const fileUploadRef = React.useRef(null);

    const handleSelect = (event) => {
        if (disabled) return;
        
        const files = Array.from(event.files);
        handleFiles(files);
    };

    const handleFiles = (files) => {
        const validFiles = files.filter(file => {
            const isHeic = file.name.toLowerCase().endsWith('.heic');
            const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
            return isHeic && isValidSize;
        });

        if (validFiles.length === 0) {
            setError('No valid HEIC files selected. Please select files with .heic extension and size less than 10MB.');
            return;
        }

        if (validFiles.length > 20) {
            setError('Maximum 20 images can be uploaded at once.');
            return;
        }

        setError('');
        onFilesUploaded(validFiles);
    };

    const itemTemplate = (file, props) => {
        // Find progress for this file
        const fileProgress = conversionProgress.find(fp => fp.filename === file.name);
        const isConverting = fileProgress !== undefined;
        
        return (
            <div className="flex align-items-center flex-wrap" style={{ width: '100%', gap: '12px' }}>
                <div className="flex align-items-center" style={{ width: '25%' }}>
                    <span>{file.name}</span>
                </div>
                <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                
                {isConverting && (
                    <div className="flex align-items-center" style={{ width: '30%', gap: '8px' }}>
                        <ProgressBar 
                            value={fileProgress.percentage} 
                            style={{ height: '6px', width: '100%' }}
                            showValue={false}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', minWidth: '40px' }}>
                            {fileProgress.percentage}%
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div style={{
                padding: '64px 20px',
                textAlign: 'center'
            }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>
                    📁
                </div>
                <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                    Drag and drop HEIC images here
                </p>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '16px' }}>
                    or click to select files
                </p>
                <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '16px' }}>
                    Supported format: .heic • Maximum file size: 10 MB • Maximum files: 20
                </p>
            </div>
        );
    };

    const chooseOptions = {
        icon: 'pi pi-fw pi-folder-open',
        iconOnly: false,
        className: 'custom-choose-btn',
        style: {  
            border: '1px solid #007bff',
            backgroundColor: 'white',
            color: '#007bff',
            cursor: 'pointer',
        },
        label: 'Browse'
    };

    const uploadOptions = {
        icon: 'pi pi-fw pi-cloud-upload',
        iconOnly: false,
        className: 'custom-upload-btn',
        style: { display: 'none' }
    };

    const cancelOptions = {
        icon: 'pi pi-fw pi-times',
        iconOnly: false,
        className: 'p-button-danger',
        style: { 
            border: '1px solid #dc2626',
            backgroundColor: 'white',
            color: '#dc2626',
            cursor: 'pointer',
            
         }
    };

    const resizeOptions = [
        { label: 'Keep Original Dimensions', value: 'original' },
        { label: 'Resize to Custom Dimensions', value: 'custom' },
        { label: 'Resize to 1920x1080', value: '1920x1080' },
        { label: 'Resize to 1280x720', value: '1280x720' },
        { label: 'Resize to 800x600', value: '800x600' }
    ];

    const qualityOptions = [
        { label: '10% (Smallest File)', value: 10 },
        { label: '20%', value: 20 },
        { label: '40%', value: 40 },
        { label: '60%', value: 60 },
        { label: '80% (Recommended)', value: 80 },
        { label: '100% (Highest Quality)', value: 100 }
    ];

    const handleResizeChange = (value) => {
        if (!disabled) {
            setResizeOption(value);
            onResizeChange(value);
        }
    };

    const handleQualityChange = (value) => {
        if (!disabled) {
            setQuality(value);
            onQualityChange(value);
        }
    };

    const headerTemplate = (options) => {
        const { className, chooseButton, uploadButton, cancelButton } = options;

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {chooseButton}
                {uploadButton}
                {cancelButton}
            </div>
        );
    };
    

    return (
        <div>
            
            <h3 className="mb-3">Upload HEIC Images</h3>
            
            <PrimeFileUpload
            
                ref={fileUploadRef}
                name="heic[]"
                multiple
                accept=".heic"
                maxFileSize={10000000}
                onSelect={handleSelect}
                onError={() => setError('Error uploading files')}
                customUpload
                auto={false}
                disabled={disabled}
                emptyTemplate={emptyTemplate}
                itemTemplate={itemTemplate}
                headerTemplate={headerTemplate}
                chooseOptions={chooseOptions}
                uploadOptions={uploadOptions}
                cancelOptions={cancelOptions}
                className="w-full fileupload-custom"
                style={{
                    opacity: disabled ? 0.6 : 1,
                    pointerEvents: disabled ? 'none' : 'auto'
                }}
                showUploadButton={false}
                showCancelButton={false}
            />

            {error && (
                <Message 
                    severity="error" 
                    text={error} 
                    className="mt-4 w-full"
                    style={{ marginTop: '16px' }}
                />
            )}

            {/* Image Conversion Options */}
           
            <div className="mt-4 pt-4 border-top-0 border-gray-300 border-opacity-50 hidden">
                <h3 className="mb-3 font-semibold">Image Conversion Options</h3>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="mb-3">
                            <label className="block mb-2 text-sm font-semibold">Resize Option</label>
                            <Dropdown 
                                value={resizeOption} 
                                options={resizeOptions}
                                onChange={(e) => handleResizeChange(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                disabled={disabled}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="mb-3">
                            <label className="block mb-2 text-sm font-semibold">Image Quality</label>
                            <Dropdown 
                                value={quality}
                                options={qualityOptions}
                                onChange={(e) => handleQualityChange(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                disabled={disabled}
                                className="w-full"
                            />
                        </div>
                        <small className="text-500 text-xs">Higher quality = larger file size</small>
                    </div>

                    {resizeOption === 'custom' && (
                        <>
                            <div className="col-12 md:col-3">
                                <div className="mb-3">
                                    <label className="block mb-2 text-sm font-semibold">Width (px)</label>
                                    <InputNumber 
                                        value={width}
                                        onValueChange={(e) => setWidth(e.value)}
                                        disabled={disabled}
                                        min={100}
                                        max={1920}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <div className="col-12 md:col-3">
                                <div className="mb-3">
                                    <label className="block mb-2 text-sm font-semibold">Height (px)</label>
                                    <InputNumber 
                                        value={height}
                                        onValueChange={(e) => setHeight(e.value)}
                                        disabled={disabled}
                                        min={100}
                                        max={5000}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileUpload;